import React from 'react';
import { motion } from 'framer-motion';
import { Database, FileScan, Network, ScanText } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const FEATURE_ICONS = [
  <ScanText className="w-6 h-6 text-zinc-100" />,
  <Database className="w-6 h-6 text-zinc-100" />,
  <Network className="w-6 h-6 text-zinc-100" />,
];

export const ComplexDataSection = () => {
  const { t } = useLanguage();
  const lang = t('complexData');
  const features = lang.features.map((f, idx) => ({ ...f, icon: FEATURE_ICONS[idx] }));

  return (
    <section id="complex-data" className="relative py-24 sm:py-32 overflow-hidden bg-zinc-950 border-t border-zinc-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-4 block">
              {lang.eyebrow}
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-zinc-100 tracking-tight mb-6">
              {lang.headline} <span className="gradient-text">{lang.headlineHighlight}</span> {lang.headlineEnd}
            </h2>
            <p className="text-lg text-zinc-400 mb-8 leading-relaxed">
              {lang.subheadline}
            </p>
            
            <div className="space-y-8">
              {features.map((feature, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-zinc-900/50 border border-zinc-800 flex items-center justify-center">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-zinc-100 mb-2">{feature.title}</h3>
                    <p className="text-zinc-400 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="w-full aspect-square md:aspect-[4/3] lg:aspect-square relative rounded-3xl border border-zinc-800 bg-zinc-900/40 overflow-hidden flex items-center justify-center"
          >
            {/* Abstract visual representing chaotic data turning into structured nodes */}
            <div className="absolute inset-0 grid-background opacity-20" />
            <div className="relative w-full h-full p-8 flex flex-col justify-center gap-6">
              {/* Top: Messy Data */}
              <div className="flex justify-around items-center opacity-60 mix-blend-screen">
                <FileScan className="w-16 h-16 text-zinc-500 rotate-12" />
                <Database className="w-12 h-12 text-zinc-600 -rotate-12 transform translate-y-4" />
                <ScanText className="w-14 h-14 text-zinc-500 rotate-6" />
              </div>
              
              {/* Middle: Doora Engine */}
              <div className="relative py-8">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-px h-full bg-gradient-to-b from-transparent via-blue-500 to-transparent opacity-50" />
                </div>
                <div className="relative z-10 w-24 h-24 mx-auto rounded-2xl bg-zinc-900 border border-zinc-700 shadow-[0_0_30px_rgba(59,130,246,0.2)] flex items-center justify-center">
                  <Network className="w-10 h-10 text-blue-400" />
                </div>
              </div>

              {/* Bottom: Structured Nodes */}
              <div className="flex justify-center items-center gap-8">
                <div className="w-3 h-3 rounded-full bg-blue-400 shadow-[0_0_10px_rgba(59,130,246,0.8)]" />
                <div className="h-px w-16 bg-blue-500/50" />
                <div className="w-4 h-4 rounded-full bg-purple-400 shadow-[0_0_10px_rgba(168,85,247,0.8)]" />
                <div className="h-px w-16 bg-purple-500/50" />
                <div className="w-3 h-3 rounded-full bg-blue-400 shadow-[0_0_10px_rgba(59,130,246,0.8)]" />
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};